/**
 * Global axios session-guard.
 *
 * Two responsibilities — both designed to stop the CRA dev overlay from
 * surfacing stray 403/401 axios rejections that escaped a `.catch()`
 * somewhere deep in the component tree:
 *
 *   1. **Response interceptor** — every axios response is intercepted.
 *      • On HTTP 401 (token expired / invalid), or on HTTP 403 returned
 *        by the canonical `GET /api/auth/me` endpoint, we PURGE the
 *        session, show a single "Session expired or invalid. Please
 *        re-login." toast, and bounce the user to `/auth`. Other 403s
 *        (e.g. Standard-tier user hitting a Pro-gated endpoint) pass
 *        through untouched so the caller's local error handler keeps
 *        its existing UX.
 *
 *   2. **Unhandled-rejection swallower** — any AxiosError that bubbles
 *      to `window.onunhandledrejection` (i.e. a floating-promise that
 *      forgot its `.catch`) is silently consumed. This is a safety net
 *      — every well-written caller already handles its own errors;
 *      this hook just stops a missed-catch from blanking the screen
 *      with the dev overlay (or, in production, from triggering a
 *      "page crashed" banner).
 *
 * Idempotent: safe to call once at app boot from index.js.
 */
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

let _installed = false;
let _purging = false;  // de-dupes overlapping 401s during a logout sweep.

function _purgeAndRedirect(reason = 'Session expired or invalid. Please re-login.') {
  if (_purging) return;
  _purging = true;
  try {
    localStorage.removeItem('token');
    localStorage.removeItem('sandbox_meta');
    localStorage.removeItem('sandbox_lead_captured');
    sessionStorage.removeItem('sandbox-banner-dismissed');
  } catch {}
  delete axios.defaults.headers.common['Authorization'];
  try { toast.error(reason); } catch {}
  // Hard navigation so every in-memory store, TanStack-Query cache,
  // and route-guard reads the empty token + clean session on boot.
  setTimeout(() => {
    if (window.location.pathname !== '/auth') {
      window.location.replace('/auth');
    }
  }, 250);
}

export function installAxiosSessionGuard() {
  if (_installed) return;
  _installed = true;

  // REQUEST INTERCEPTOR — always attach the Bearer token freshly from
  // localStorage on every outbound request. This eliminates a mount-
  // time race we saw on production: SignaturesSection mounted and
  // fired `GET /api/document-preferences` BEFORE AuthContext's
  // useEffect had populated `axios.defaults.headers.common.Authorization`
  // from the token in localStorage — so the call went out header-less
  // and the backend's `HTTPBearer(auto_error=True)` returned 403,
  // triggering "Failed to load signatures".
  //
  // We respect any Authorization header already set on the per-request
  // config (e.g. admin API client explicitly attaches its own token)
  // so this is purely additive.
  axios.interceptors.request.use(
    (config) => {
      const alreadySet =
        config.headers &&
        (config.headers.Authorization || config.headers.authorization);
      if (!alreadySet) {
        try {
          const token = localStorage.getItem('token');
          if (token) {
            config.headers = config.headers || {};
            config.headers.Authorization = `Bearer ${token}`;
          }
        } catch {
          // Safari private mode / storage disabled — fall through
          // header-less; the response interceptor handles the 401/403.
        }
      }
      return config;
    },
  );

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      const url = error?.config?.url || '';
      const hasToken = !!localStorage.getItem('token');
      const isAuthMe = url.includes('/auth/me');

      // SESSION-CORRUPTION REDIRECT — fires ONLY when the canonical
      // `/auth/me` probe itself returns 401/403. That's the definitive
      // "this token is dead" signal — every other 401/403 is treated as
      // a normal application error so transient races (e.g. an
      // in-flight request firing during a header swap) don't blow up
      // the whole session.
      //
      // We also DON'T fire while sitting on /auth — purging a session
      // the user is already trying to log into would yank the login
      // form mid-submit.
      const onAuthPage = window.location.pathname.startsWith('/auth');
      if (
        hasToken &&
        !onAuthPage &&
        isAuthMe &&
        (status === 401 || status === 403)
      ) {
        _purgeAndRedirect();
      }
      return Promise.reject(error);
    },
  );

  // Final safety net — swallow uncaught AxiosError so the CRA overlay
  // and production "page crashed" banner stay silent. Only AxiosError
  // is swallowed; genuine app bugs (TypeErrors, ReferenceErrors etc.)
  // still surface as normal.
  window.addEventListener('unhandledrejection', (ev) => {
    const reason = ev.reason || {};
    const isAxios = !!(reason.isAxiosError || (reason.name === 'AxiosError'));
    if (isAxios) {
      ev.preventDefault();
      // Eslint hint — don't make this an interactive error surface.
      // The caller forgot a `.catch`; we treat it as a no-op so the
      // app keeps running. Status-401-driven logout is already
      // handled above via the response interceptor.
    }
  });
}

// Avoid an "imported but unused" warning when only the side-effect is needed.
export { API };
